<template>
    <div id="app">
        <div class="app-container">
            <!-- 头部 -->
            <div class="header">
                <h1>💬 WebSocket 实时聊天室</h1>
                <p>基于 ASP.NET Web API + Vue2 构建</p>
            </div>

            <!-- 状态栏 -->
            <div class="status-bar">
                <div class="status-info">
                    <div class="status-indicator">
                        <div :class="['status-dot', connectionStatusClass]"></div>
                        <span>{{ connectionStatusText }}</span>
                    </div>
                    <div class="user-info" v-if="clientId">
                        ID: {{ clientId }} | 用户: {{ userName }}
                    </div>
                </div>
                <div class="online-count">
                    👥 在线用户: {{ onlineUsers }}
                </div>
            </div>

            <div class="main-content">
                <!-- 聊天区域 -->
                <div class="chat-area">
                    <!-- 消息容器 -->
                    <div class="messages-container" ref="messagesContainer">
                        <div v-for="(msg, index) in messages" :key="index"
                             :class="['message', getMessageClass(msg)]">

                            <!-- 系统消息 -->
                            <div v-if="msg.type === 'system'" class="message-system">
                                {{ msg.content }}
                            </div>

                            <!-- 信息消息 -->
                            <div v-else-if="msg.type === 'info'" class="message-info">
                                {{ msg.content }}
                            </div>

                            <!-- 聊天消息 -->
                            <div v-else :class="msg.direction === 'sent' ? 'message-sent' : 'message-received'">
                                <div class="message-header">
                  <span class="message-user" v-if="msg.direction === 'received'">
                    {{ msg.userName }}
                  </span>
                                    <span class="message-user" v-else>我</span>
                                    <span class="message-time">{{ formatTime(msg.timestamp) }}</span>
                                </div>
                                <div class="message-content">{{ msg.content }}</div>
                            </div>
                        </div>

                        <!-- 输入指示器 -->
                        <div v-if="typingUsers.length > 0" class="typing-indicator">
                            ✍️ {{ typingUsers.join(', ') }} 正在输入...
                        </div>

                        <!-- 空状态 -->
                        <div v-if="messages.length === 0" class="empty-state">
                            🎯 连接后开始聊天吧！
                        </div>
                    </div>

                    <!-- 输入区域 -->
                    <div class="input-area">
                        <!-- 用户名输入 -->
                        <div class="input-group">
                            <input
                                    type="text"
                                    v-model="userName"
                                    placeholder="请输入您的用户名..."
                                    class="user-input"
                                    :disabled="isConnected">
                            <button @click="updateUserName" class="btn btn-secondary" :disabled="isConnected">
                                📝 更新
                            </button>
                        </div>

                        <!-- 消息输入 -->
                        <div class="input-group">
                            <input
                                    type="text"
                                    v-model="currentMessage"
                                    @keyup.enter="sendMessage"
                                    @input="onTyping"
                                    @blur="onStopTyping"
                                    placeholder="输入消息并按回车发送..."
                                    class="user-input"
                                    :disabled="!isConnected">
                            <button @click="sendMessage" class="btn btn-primary" :disabled="!isConnected">
                                📤 发送
                            </button>
                        </div>

                        <!-- 控制按钮 -->
                        <div class="control-buttons">
                            <button @click="connect" class="btn btn-success" :disabled="isConnected || isConnecting">
                                <span v-if="isConnecting">🔄 连接中...</span>
                                <span v-else>🔗 连接</span>
                            </button>
                            <button @click="disconnect" class="btn btn-danger" :disabled="!isConnected">
                                🔌 断开
                            </button>
                            <button @click="clearMessages" class="btn btn-sm btn-secondary">
                                🗑️ 清空
                            </button>
                            <button @click="sendCommand('/time')" class="btn btn-sm" :disabled="!isConnected">
                                ⏰ 时间
                            </button>
                            <button @click="sendCommand('/users')" class="btn btn-sm" :disabled="!isConnected">
                                👥 用户
                            </button>
                            <button @click="sendCommand('/help')" class="btn btn-sm" :disabled="!isConnected">
                                ❓ 帮助
                            </button>
                        </div>
                    </div>
                </div>

                <!-- 侧边栏 -->
                <div class="sidebar">
                    <!-- 统计信息 -->
                    <div class="sidebar-section">
                        <h3>📊 统计信息</h3>
                        <div class="stats-grid">
                            <div class="stat-card">
                                <div class="stat-number">{{ messageCount }}</div>
                                <div class="stat-label">总消息</div>
                            </div>
                            <div class="stat-card">
                                <div class="stat-number">{{ sentCount }}</div>
                                <div class="stat-label">已发送</div>
                            </div>
                            <div class="stat-card">
                                <div class="stat-number">{{ receivedCount }}</div>
                                <div class="stat-label">已接收</div>
                            </div>
                            <div class="stat-card">
                                <div class="stat-number">{{ onlineUsers }}</div>
                                <div class="stat-label">在线</div>
                            </div>
                        </div>
                    </div>

                    <!-- 在线用户 -->
                    <div class="sidebar-section">
                        <h3>👥 在线用户</h3>
                        <div class="user-list">
                            <div v-for="user in onlineUserList" :key="user.clientId" class="user-item">
                                <div class="user-avatar">
                                    {{ user.userName ? user.userName.charAt(0).toUpperCase() : 'U' }}
                                </div>
                                <div class="user-details">
                                    <div class="user-name">{{ user.userName || '匿名用户' }}</div>
                                    <div class="user-id">{{ user.clientId }}</div>
                                </div>
                            </div>
                            <div v-if="onlineUserList.length === 0" class="empty-state">
                                暂无在线用户
                            </div>
                        </div>
                    </div>

                    <!-- 命令列表 -->
                    <div class="sidebar-section">
                        <h3>⚡ 快捷命令</h3>
                        <div class="command-list">
                            <div class="command-item">
                                <div class="command-name">/time</div>
                                <div class="command-desc">获取服务器时间</div>
                            </div>
                            <div class="command-item">
                                <div class="command-name">/users</div>
                                <div class="command-desc">查看在线用户数</div>
                            </div>
                            <div class="command-item">
                                <div class="command-name">/help</div>
                                <div class="command-desc">显示帮助信息</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'WebSocketChat',
        data() {
            return {
                // WebSocket 连接状态
                websocket: null,
                isConnected: false,
                isConnecting: false,
                clientId: null,

                // 用户信息
                userData:[],
                userName: '',
                currentMessage: '',

                // 消息数据
                messages: [],
                typingUsers: [],
                onlineUserList: [],
                onlineUsers: 0,

                // 统计信息
                messageCount: 0,
                sentCount: 0,
                receivedCount: 0,

                // 配置
                reconnectAttempts: 0,
                maxReconnectAttempts: 5
            }
        },
        computed: {
            connectionStatusClass() {
                if (this.isConnected) return 'status-connected'
                if (this.isConnecting) return 'status-connecting'
                return 'status-disconnected'
            },
            connectionStatusText() {
                if (this.isConnected) return '已连接'
                if (this.isConnecting) return '连接中...'
                return '未连接'
            }
        },
        // 生命周期
        mounted() {
            let that = this
            that.userData = JSON.parse(sessionStorage.getItem('userData'));
            that.userName = that.userData.username;
            this.addSystemMessage('🚀 欢迎使用 WebSocket 聊天室！')
            this.addSystemMessage('💡 请点击"连接"按钮开始聊天')

        },
        beforeDestroy() {
            this.disconnect()
        },
        methods: {
            // 连接 WebSocket
            connect() {
                if (this.isConnected || this.isConnecting) return

                this.isConnecting = true
                this.reconnectAttempts = 0

                try {
                    // 构建 WebSocket URL
                    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
                    const basePath = window.location.pathname.split('/').slice(0, -1).join('/') || ''
                    const wsUrl = `${protocol}//localhost:9091${basePath}/api/websocket/connect?userName=${encodeURIComponent(this.userName)}`

                    console.log('🔄 连接地址:', wsUrl)

                    this.websocket = new WebSocket(wsUrl)

                    this.websocket.onopen = () => {
                        this.isConnected = true
                        this.isConnecting = false
                        this.reconnectAttempts = 0
                        this.addSystemMessage('✅ WebSocket 连接已建立')
                    }

                    this.websocket.onmessage = (event) => {
                        try {
                            const data = JSON.parse(event.data)
                            this.handleWebSocketMessage(data)
                        } catch (e) {
                            // 如果不是 JSON 格式，直接显示
                            this.addSystemMessage('📨 ' + event.data)
                        }
                    }

                    this.websocket.onclose = (event) => {
                        this.isConnected = false
                        this.isConnecting = false
                        this.clientId = null

                        if (event.code !== 1000) {
                            this.addSystemMessage('❌ 连接异常关闭，代码: ' + event.code)
                            this.attemptReconnect()
                        } else {
                            this.addSystemMessage('🔒 连接已正常关闭')
                        }
                    }

                    this.websocket.onerror = (error) => {
                        this.isConnecting = false
                        console.error('WebSocket 错误:', error)
                        this.addSystemMessage('❌ 连接发生错误')
                    }

                } catch (error) {
                    this.isConnecting = false
                    console.error('创建连接失败:', error)
                    this.addSystemMessage('❌ 创建连接失败: ' + error.message)
                }
            },

            // 尝试重新连接
            attemptReconnect() {
                if (this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.reconnectAttempts++
                    const delay = Math.min(1000 * this.reconnectAttempts, 10000)
                    this.addSystemMessage(`🔄 ${delay/1000}秒后尝试重新连接 (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

                    setTimeout(() => {
                        if (!this.isConnected) {
                            this.connect()
                        }
                    }, delay)
                } else {
                    this.addSystemMessage('❌ 重连次数已达上限，请手动重新连接')
                }
            },

            // 断开连接
            disconnect() {
                if (this.websocket) {
                    this.websocket.close(1000, '用户主动断开')
                    this.websocket = null
                }
            },

            // 处理 WebSocket 消息
            handleWebSocketMessage(data) {
                switch (data.type) {
                    case 'welcome':
                        this.clientId = data.clientId
                        this.userName = data.userName
                        this.onlineUsers = data.onlineUsers
                        this.addSystemMessage('🎉 ' + data.message)
                        break

                    case 'chat_message':
                        this.addMessage({
                            type: 'chat',
                            direction: 'received',
                            userName: data.userName,
                            content: data.content,
                            timestamp: new Date(data.timestamp)
                        })
                        break

                    case 'user_joined':
                        this.addSystemMessage('👋 ' + data.message)
                        this.onlineUsers = data.onlineUsers
                        break

                    case 'user_left':
                        this.addSystemMessage('👋 ' + data.message)
                        this.onlineUsers = data.onlineUsers
                        break

                    case 'system':
                        this.addSystemMessage('ℹ️ ' + data.message)
                        break

                    default:
                        console.log('未知消息类型:', data)
                        break
                }
            },

            // 发送消息
            sendMessage() {
                if (!this.currentMessage.trim() || !this.isConnected) return

                this.websocket.send(this.currentMessage)

                this.addMessage({
                    type: 'chat',
                    direction: 'sent',
                    userName: this.userName,
                    content: this.currentMessage,
                    timestamp: new Date()
                })

                this.currentMessage = ''
                this.onStopTyping()
            },

            // 发送命令
            sendCommand(command) {
                if (this.isConnected) {
                    this.websocket.send(command)
                }
            },

            // 更新用户名
            updateUserName() {
                if (this.userName.trim() && !this.isConnected) {
                    this.addSystemMessage('✅ 用户名已更新为: ' + this.userName)
                }
            },

            // 输入状态处理
            onTyping() {
                // 可以在这里实现输入状态通知
            },

            onStopTyping() {
                // 可以在这里实现停止输入状态通知
            },

            // 添加消息到列表
            addMessage(message) {
                this.messages.push(message)
                this.messageCount++
                if (message.direction === 'sent') this.sentCount++
                if (message.direction === 'received') this.receivedCount++
                this.scrollToBottom()
            },

            // 添加系统消息
            addSystemMessage(content) {
                this.messages.push({
                    type: 'system',
                    content: content,
                    timestamp: new Date()
                })
                this.messageCount++
                this.scrollToBottom()
            },

            // 清空消息
            clearMessages() {
                this.messages = []
                this.messageCount = 0
                this.sentCount = 0
                this.receivedCount = 0
            },

            // 滚动到底部
            scrollToBottom() {
                this.$nextTick(() => {
                    const container = this.$refs.messagesContainer
                    if (container) {
                        container.scrollTop = container.scrollHeight
                    }
                })
            },

            // 获取消息样式类
            getMessageClass(msg) {
                if (msg.type === 'system' || msg.type === 'user_joined' || msg.type === 'user_left') {
                    return 'message-system'
                }
                if (msg.type === 'info') {
                    return 'message-info'
                }
                return ''
            },

            // 格式化时间
            formatTime(timestamp) {
                const date = new Date(timestamp)
                return date.toLocaleTimeString('zh-CN', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                })
            }
        },
    }
</script>

<style lang="less" scoped>
    // 变量定义
    @primary-color: #007bff;
    @success-color: #28a745;
    @danger-color: #dc3545;
    @warning-color: #ffc107;
    @info-color: #17a2b8;
    @dark-color: #343a40;
    @light-color: #f8f9fa;
    @border-color: #e9ecef;
    @text-muted: #6c757d;

    // 混入
    .gradient-bg(@start-color, @end-color) {
        background: linear-gradient(135deg, @start-color, @end-color);
    }

    .flex-center() {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .card-shadow() {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    // 样式
    #app {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        min-height: 100vh;
        padding: 20px;
    }

    .app-container {
        max-width: 1200px;
        margin: 0 auto;
        background: white;
        border-radius: 15px;
        .card-shadow();
        overflow: hidden;
    }

    .header {
        .gradient-bg(#2c3e50, #34495e);
        color: white;
        padding: 25px;
        text-align: center;

        h1 {
            font-size: 2.2em;
            margin-bottom: 8px;
            font-weight: 600;
        }

        p {
            opacity: 0.9;
            font-size: 1.1em;
            margin: 0;
        }
    }

    .status-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 25px;
        background: @light-color;
        border-bottom: 1px solid @border-color;

        .status-info {
            .flex-center();
            gap: 15px;
        }

        .status-indicator {
            .flex-center();
            gap: 8px;
            font-weight: 500;
        }

        .status-dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
        }

        .status-connected {
            background: @success-color;
        }

        .status-disconnected {
            background: @danger-color;
        }

        .status-connecting {
            background: @warning-color;
        }

        .user-info {
            color: @text-muted;
            font-weight: 500;
        }

        .online-count {
            font-weight: 500;
            color: @dark-color;
        }
    }

    .main-content {
        display: grid;
        grid-template-columns: 1fr 300px;
        min-height: 600px;
    }

    .chat-area {
        display: flex;
        flex-direction: column;
        border-right: 1px solid @border-color;
    }

    .messages-container {
        flex: 1;
        padding: 20px;
        overflow-y: auto;
        background: @light-color;

        .message {
            margin-bottom: 16px;
            padding: 12px 16px;
            border-radius: 12px;
            max-width: 80%;
            animation: fadeInUp 0.3s ease;

            @keyframes fadeInUp {
                from {
                    opacity: 0;
                    transform: translateY(10px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
        }

        .message-sent {
            .gradient-bg(@primary-color, darken(@primary-color, 10%));
            color: white;
            margin-left: auto;
            border-bottom-right-radius: 4px;
        }

        .message-received {
            background: white;
            border: 1px solid @border-color;
            border-bottom-left-radius: 4px;
            .card-shadow();
        }

        .message-system {
            background: lighten(@warning-color, 45%);
            color: darken(@warning-color, 30%);
            text-align: center;
            max-width: 100%;
            font-style: italic;
            border: 1px solid lighten(@warning-color, 30%);
            margin: 10px auto;
        }

        .message-info {
            background: lighten(@info-color, 50%);
            color: darken(@info-color, 20%);
            border: 1px solid lighten(@info-color, 40%);
        }

        .message-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 6px;
        }

        .message-user {
            font-weight: 600;
            font-size: 0.9em;
        }

        .message-time {
            font-size: 0.8em;
            opacity: 0.7;
        }

        .message-content {
            line-height: 1.4;
        }

        .typing-indicator {
            font-style: italic;
            color: @text-muted;
            font-size: 0.9em;
            margin: 10px 0;
            min-height: 20px;
        }

        .empty-state {
            text-align: center;
            color: @text-muted;
            padding: 30px;
            font-style: italic;
        }
    }

    .input-area {
        padding: 25px;
        background: white;
        border-top: 1px solid @border-color;

        .input-group {
            display: flex;
            gap: 12px;
            margin-bottom: 15px;
        }

        .user-input {
            flex: 1;
            padding: 14px 18px;
            border: 2px solid @border-color;
            border-radius: 25px;
            outline: none;
            font-size: 14px;
            transition: all 0.3s ease;

            &:focus {
                border-color: @primary-color;
                box-shadow: 0 0 0 3px fade(@primary-color, 10%);
            }

            &:disabled {
                background-color: @light-color;
                cursor: not-allowed;
            }
        }

        .btn {
            padding: 14px 28px;
            border: none;
            border-radius: 25px;
            cursor: pointer;
            font-weight: 600;
            font-size: 14px;
            transition: all 0.3s ease;
            .flex-center();
            gap: 6px;

            &:hover:not(:disabled) {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            }

            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
                transform: none !important;
                box-shadow: none !important;
            }
        }

        .btn-primary {
            .gradient-bg(@primary-color, darken(@primary-color, 10%));
            color: white;
        }

        .btn-success {
            .gradient-bg(@success-color, darken(@success-color, 10%));
            color: white;
        }

        .btn-danger {
            .gradient-bg(@danger-color, darken(@danger-color, 10%));
            color: white;
        }

        .btn-secondary {
            background: @text-muted;
            color: white;
        }

        .control-buttons {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
        }

        .btn-sm {
            padding: 10px 20px;
            font-size: 13px;
        }
    }

    .sidebar {
        padding: 25px;
        background: @light-color;

        .sidebar-section {
            margin-bottom: 25px;
        }

        h3 {
            color: @dark-color;
            margin-bottom: 15px;
            font-size: 1.2em;
            .flex-center();
            gap: 8px;
        }

        .stats-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 20px;
        }

        .stat-card {
            background: white;
            padding: 15px;
            border-radius: 10px;
            text-align: center;
            .card-shadow();

            .stat-number {
                font-size: 1.8em;
                font-weight: bold;
                color: @primary-color;
                margin-bottom: 5px;
            }

            .stat-label {
                font-size: 0.9em;
                color: @text-muted;
            }
        }

        .user-list {
            max-height: 200px;
            overflow-y: auto;
        }

        .user-item {
            .flex-center();
            gap: 12px;
            padding: 12px;
            margin-bottom: 8px;
            background: white;
            border-radius: 10px;
            .card-shadow();

            .user-avatar {
                width: 36px;
                height: 36px;
                border-radius: 50%;
                .gradient-bg(@primary-color, darken(@primary-color, 10%));
                .flex-center();
                color: white;
                font-weight: bold;
                font-size: 14px;
            }

            .user-details {
                flex: 1;
            }

            .user-name {
                font-weight: 600;
                color: @dark-color;
            }

            .user-id {
                font-size: 0.8em;
                color: @text-muted;
            }
        }

        .command-list {
            background: white;
            padding: 15px;
            border-radius: 10px;
            .card-shadow();

            .command-item {
                padding: 8px 0;
                border-bottom: 1px solid lighten(@border-color, 5%);

                &:last-child {
                    border-bottom: none;
                }

                .command-name {
                    font-weight: 600;
                    color: @primary-color;
                }

                .command-desc {
                    font-size: 0.9em;
                    color: @text-muted;
                }
            }
        }
    }

    // 响应式设计
    @media (max-width: 768px) {
        .main-content {
            grid-template-columns: 1fr;
        }

        .chat-area {
            border-right: none;
            border-bottom: 1px solid @border-color;
        }

        .status-bar {
            flex-direction: column;
            gap: 10px;
            align-items: flex-start;
        }

        .control-buttons {
            justify-content: center;
        }

        .message {
            max-width: 90%;
        }

        #app {
            padding: 10px;
        }
    }
</style>